import "@/App.css";
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Dashboard from "./pages/Dashboard";
import NewProcurement from "./pages/NewProcurement";
import IntakeSelection from "./pages/IntakeSelection";
import ProcurementDetail from "./pages/ProcurementDetail";
import PolicyViewer from "./pages/PolicyViewer";
import ContinueProcurement from "./pages/ContinueProcurement";
import MasterDataManagement from "./pages/MasterDataManagement";
import Login from "./pages/Login";
import AcceptInvite from './pages/AcceptInvite';
import AuthCallback from "./pages/AuthCallback";
import About from "./pages/About";

// Root layout route - AuthProvider must wrap every route (ProtectedRoute
// reads its context), so it lives here instead of outside the router.
const RootLayout = () => (
  <AuthProvider>
    <Outlet />
  </AuthProvider>
);

// Unsaved-changes navigation guard (pre-merge item): a data router
// (createBrowserRouter/RouterProvider), not the classic <BrowserRouter>, is
// required for react-router-dom's useBlocker - WizardContext's guard calls
// it to intercept in-app navigation away from a dirty wizard step. This is
// the only change in this file driven by that feature; every route below
// is unchanged from the previous <Routes> tree.
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/reset-password" element={<AcceptInvite mode="reset" />} />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/new" element={
        <ProtectedRoute>
          <IntakeSelection />
        </ProtectedRoute>
      } />
      <Route path="/new-procurement" element={
        <ProtectedRoute>
          <NewProcurement />
        </ProtectedRoute>
      } />
      <Route path="/continue/:id" element={
        <ProtectedRoute>
          <ContinueProcurement />
        </ProtectedRoute>
      } />
      <Route path="/continue-procurement/:id" element={
        <ProtectedRoute>
          <ContinueProcurement />
        </ProtectedRoute>
      } />
      <Route path="/procurement/:id" element={
        <ProtectedRoute>
          <ProcurementDetail />
        </ProtectedRoute>
      } />
      <Route path="/policy" element={
        <ProtectedRoute>
          <PolicyViewer />
        </ProtectedRoute>
      } />
      <Route path="/about" element={
        <ProtectedRoute>
          <About />
        </ProtectedRoute>
      } />
      <Route path="/admin/master-data" element={
        <ProtectedRoute>
          <MasterDataManagement />
        </ProtectedRoute>
      } />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  )
);

function App() {
  return (
    <div className="App min-h-screen bg-white">
      <RouterProvider router={router} />
      {/* Item 4: placement (top-center) and severity styling are owned by
          the Toaster component itself - see components/ui/sonner.jsx. */}
      <Toaster />
    </div>
  );
}

export default App;
